import React from 'react'
import PropTypes from 'prop-types'
import ModuleContainer from '../../components/cms/module-container'
import StickyStack from '../../components/cms/sticky-stack'
import {AspectRatio, Box, Text, Button} from '@chakra-ui/react3'
import Copy from '../../components/cms/copy'
import PriceButtonGroup from '../../components/cms/price-button-group'
import Image from '../../components/cms/image'
import {useReducedMotion} from 'framer-motion'
import {AddIcon} from '../../components/icons'
import useCTAAction from '../../hooks/useCtaAction'
import {useIntl} from 'react-intl'

const Highlight = ({
    subline,
    headline,
    copy,
    priceProduct,
    cta,
    image,
    headingLevel,
    trackingContext,
    type,
    noAnimation = false,
    overlay
}) => {
    const {formatMessage} = useIntl()
    const ctaProps = useCTAAction({
        action: {type: 'overlay', value: overlay}
    })

    return (
        <>
            <Box
                role="group"
                aria-label={type}
                display="flex"
                flexDirection={{base: 'column', s: 'row'}}
                backgroundColor="var(--module-surface-bg-color)"
                borderRadius="m"
                overflow="hidden"
                position="relative"
                mb={noAnimation ? 'pacing-16' : undefined}
            >
                <Box
                    pt={{base: 'spacing-40', s: 'spacing-64'}}
                    pb={{base: 'spacing-40', s: 'spacing-64'}}
                    pl={{base: 'spacing-24', s: '74px'}}
                    pr={{base: 'spacing-24', s: '22px'}}
                    display="flex"
                    flexDirection="column"
                    width="fit-content"
                >
                    <Box width={{base: '100%', xs: '75%', s: 319}} flex="1">
                        <Box
                            as={`h${headingLevel + 1}`}
                            display="flex"
                            flexDirection="column"
                            justifyContent="space-between"
                            height="100%"
                        >
                            <Text color="text.brand-default" textStyle="body.small">
                                {subline}
                            </Text>
                            <Text
                                mt={{base: 'spacing-40', s: 'auto'}}
                                mb={{base: 'spacing-24', s: 'spacing-32'}}
                                color="text.primary"
                                textStyle="display.small"
                            >
                                {headline}
                            </Text>
                        </Box>
                    </Box>
                    <Box width={{base: '100%', xs: '75%', s: 319}} flex="1">
                        <Copy {...copy} />
                    </Box>
                    <Box mt={{base: 'spacing-40', s: 'auto'}}>
                        <PriceButtonGroup
                            price={priceProduct}
                            cta={cta}
                            size="small"
                            trackingContext={trackingContext}
                        />
                    </Box>
                </Box>
                <Box flex={1}>
                    <AspectRatio ratio={4 / 3}>
                        <Image {...image} widths={{base: '100vw', s: '941px'}} objectFit="cover" />
                    </AspectRatio>
                </Box>
                {overlay && (
                    <Box position="absolute" top="spacing-24" right="spacing-24">
                        <Button
                            {...ctaProps}
                            size="md"
                            variant="primaryFilled"
                            aria-label={formatMessage({
                                defaultMessage: 'More Information, opens Overlay',
                                id: 'leap.highlights.assitive_msg.more_information'
                            })}
                        >
                            <AddIcon />
                        </Button>
                    </Box>
                )}
            </Box>
        </>
    )
}

const highlightProps = {
    subline: PropTypes.string,
    headline: PropTypes.string,
    type: PropTypes.string,
    copy: PropTypes.object,
    priceProduct: PropTypes.object,
    cta: PropTypes.object,
    image: PropTypes.shape({
        src: PropTypes.string,
        alt: PropTypes.string,
        text: PropTypes.string
    }),
    trackingContext: PropTypes.shape({
        moduleName: PropTypes.string
    }),
    headingLevel: PropTypes.number,
    noAnimation: PropTypes.bool,
    overlay: PropTypes.string
}

Highlight.propTypes = highlightProps

const HighlightsStackModule = React.forwardRef(
    (
        {
            moduleId,
            moduleType,
            pageId,
            anchor,
            background,
            highlights,
            headingLevel,
            enableAnimation
        },
        ref
    ) => {
        const shouldReduceMotion = useReducedMotion()
        // don't show animation if animation is disabled or motion should be reduced
        const noAnimation = !enableAnimation || shouldReduceMotion

        return (
            <ModuleContainer
                p={4}
                moduleId={moduleId}
                moduleType={moduleType}
                pageId={pageId}
                anchor={anchor}
                background={background}
                spacing="none"
                ref={ref}
            >
                <StickyStack.Container noAnimation={noAnimation}>
                    {highlights.map((highlight, index) => (
                        <StickyStack.Item key={index}>
                            <Highlight
                                headingLevel={headingLevel}
                                noAnimation={noAnimation}
                                {...highlight}
                                trackingContext={{moduleName: moduleType, eventLabel: 'cta'}}
                            />
                        </StickyStack.Item>
                    ))}
                </StickyStack.Container>
            </ModuleContainer>
        )
    }
)

if (process.env.NODE_ENV !== 'production') {
    HighlightsStackModule.displayName = 'HighlightsStackModule'
}

HighlightsStackModule.propTypes = {
    anchor: PropTypes.string,
    background: PropTypes.oneOf(['default', 'subtle', 'strong', 'inverted']),
    headingLevel: PropTypes.number,
    highlights: PropTypes.arrayOf(PropTypes.shape(highlightProps)).isRequired,
    moduleId: PropTypes.string,
    moduleType: PropTypes.string,
    pageId: PropTypes.string,
    enableAnimation: PropTypes.oneOf([true, false])
}

export default HighlightsStackModule
